/**
 * SettingsPage — tabbed settings with connections (configure + verify) and browser engine.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useSettingsStore } from "@/stores/settingsStore";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/common/Button";
import { McpServersSection } from "@/components/settings/McpServersSection";
import { SkillsSection } from "@/components/settings/SkillsSection";
import { ProvisionedKeysSection } from "@/components/settings/ProvisionedKeysSection";
import { NbyulaCliSection } from "@/components/settings/NbyulaCliSection";
import { AppearanceToggle } from "@/components/settings/AppearanceToggle";
import { AnthropicLogo, OpenRouterLogo } from "@/components/common/ProviderLogos";
import { apiFetch } from "@/api/client";

// --- Field definitions ---

const SECRET_FIELDS = [
  { key: "claude_token", label: "Claude Token", placeholder: "sk-ant-oat01-...", group: "ai" },
  // The single non-Anthropic gateway. Selecting any ``openrouter/...``
  // model from the chat dropdown routes through it ("one API, all models").
  // Saving this hot-reloads the LiteLLM proxy server-side, no restart needed.
  // Direct OpenAI / Gemini keys are intentionally not offered — those models
  // are reached via OpenRouter.
  { key: "openrouter_api_key", label: "OpenRouter API Key", placeholder: "sk-or-...", group: "ai" },
  { key: "opensearch_password", label: "OpenSearch Password", placeholder: "password", group: "data" },
  { key: "nbyula_gateway_api_key", label: "Nbyula MCP API Key", placeholder: "xxxxxxxx-xxxx-...", group: "data" },
];

const CONFIG_FIELDS = [
  { key: "opensearch_username", label: "OpenSearch Username", default: "admin", group: "data" },
  { key: "opensearch_host", label: "OpenSearch Host", default: "search.api.nbyula.com", group: "data" },
  { key: "opensearch_port", label: "OpenSearch Port", default: "443", group: "data" },
  // Deep-research model — routes through OpenRouter as ``perplexity/<model>``.
  { key: "pplx_model", label: "Research Model (Sonar)", default: "sonar-deep-research", group: "ai" },
  { key: "max_agent_turns", label: "Max Agent Turns", default: "50", group: "ai" },
];

// ``icon`` is either an emoji string or a React element. Provider logos
// (Anthropic, OpenRouter) get the official mark; the rest stay on emoji since
// OpenSearch doesn't ship a clean public-domain SVG we'd want to inline.
const CONNECTION_CHECKS: { key: string; label: string; icon: ReactNode }[] = [
  { key: "claude_token",            label: "Claude API",     icon: <AnthropicLogo className="w-5 h-5" /> },
  { key: "openrouter_api_key",      label: "OpenRouter",     icon: <OpenRouterLogo className="w-5 h-5" /> },
  { key: "opensearch_password",     label: "OpenSearch",     icon: "🗄️" },
  { key: "nbyula_gateway_api_key",  label: "Nbyula Gateway", icon: "🌐" },
  { key: "discovery_connection",    label: "nbyula.com",     icon: "🔗" },
];

/**
 * Where a credential came from. "Configured" alone was too coarse to debug
 * with — a deleted org token still left every row green while desktops had
 * stopped syncing and chat had moved to OpenRouter (ADR-0014 amendment 6).
 */
type CredentialSource = "personal" | "shared" | "cloud" | "local" | "none";

type CredentialSources = {
  sources: Record<string, { configured: boolean; source: CredentialSource }>;
  claude_pool: { label: string; active: boolean; available: boolean; cooling_until: string | null }[];
  cloud: boolean;
};

/** Badge copy per provenance. `null` ⇒ nothing worth saying (unconfigured). */
const SOURCE_BADGE: Record<CredentialSource, { text: string; tone: string } | null> = {
  personal: { text: "Your key", tone: "bg-space-20 text-space-60" },
  shared: { text: "Shared · org", tone: "bg-jove/10 text-jove" },
  cloud: { text: "Shared · synced from cloud", tone: "bg-emerald-500/10 text-emerald-600" },
  local: { text: "Set on this device", tone: "bg-space-20 text-space-60" },
  none: null,
};

/**
 * One line describing the shared Claude subscriptions.
 *
 * The distinction that matters: "no token" and "every subscription capped"
 * both stop Claude, but only one is fixed by pasting a credential. Naming the
 * soonest reset tells you when it comes back on its own.
 */
function describePool(pool: CredentialSources["claude_pool"]): string {
  const free = pool.filter((p) => p.available).length;
  if (free > 0) {
    return `${free} of ${pool.length} subscription${pool.length === 1 ? "" : "s"} available`;
  }
  const soonest = pool
    .map((p) => p.cooling_until)
    .filter((t): t is string => !!t)
    .sort()[0];
  const when = soonest
    ? new Date(soonest).toLocaleString(undefined, {
        weekday: "short", hour: "numeric", minute: "2-digit",
      })
    : null;
  return when
    ? `All ${pool.length} subscriptions capped — next reset ${when}`
    : `All ${pool.length} subscriptions capped`;
}

/** Maps each connection key to its related secret + config field keys. */
const CONNECTION_FIELD_MAP: Record<string, { secrets: string[]; configs: string[] }> = {
  claude_token: { secrets: ["claude_token"], configs: ["max_agent_turns"] },
  // OpenRouter powers web research (Perplexity sonar) too, so the research
  // model selector lives under its card now that the native key is gone.
  openrouter_api_key: { secrets: ["openrouter_api_key"], configs: ["pplx_model"] },
  opensearch_password: { secrets: ["opensearch_password"], configs: ["opensearch_username", "opensearch_host", "opensearch_port"] },
  nbyula_gateway_api_key: { secrets: ["nbyula_gateway_api_key"], configs: [] },
  discovery_connection: { secrets: [], configs: [] },
};

type Tab = "general" | "connections" | "mcp" | "skills" | "advanced";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "general", label: "General", icon: "👤" },
  { id: "connections", label: "Connections", icon: "🔗" },
  { id: "mcp", label: "MCP Servers", icon: "🔌" },
  { id: "skills", label: "Skills", icon: "📚" },
  { id: "advanced", label: "Advanced", icon: "⚙️" },
];

export function SettingsPage() {
  const {
    settings, loadSettings, isFetching, cloudMode, canEditGlobal,
  } = useSettingsStore();
  const user = useAuthStore((s) => s.user);
  const role = user?.role;

  const [tab, setTab] = useState<Tab>("general");

  // Connection check state
  const [connResults, setConnResults] = useState<Record<string, { ok: boolean; text: string; checking: boolean }>>({});

  // Configure modal state
  const [configureKey, setConfigureKey] = useState<string | null>(null);

  const [systemInfo, setSystemInfo] = useState<{
    app_version: string; python_version: string; platform: string;
    arch: string; frozen: boolean; packages: Record<string, string>;
  } | null>(null);

  // MCP re-init
  const [mcpReinitializing, setMcpReinitializing] = useState(false);
  const [mcpMessage, setMcpMessage] = useState<{ ok: boolean; text: string } | null>(null);

  // Cloud sync status (Advanced) — last push/pull, last error, manual trigger.
  const [syncStatus, setSyncStatus] = useState<{
    role: "cloud" | "desktop";
    last_push_at?: string | null; last_push_error?: string | null; last_push_error_at?: string | null;
    last_pull_at?: string | null; last_pull_error?: string | null; last_pull_error_at?: string | null;
    pending_changes?: number;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const loadSyncStatus = useCallback(() => {
    apiFetch<NonNullable<typeof syncStatus>>("/sync/status").then(setSyncStatus).catch(() => {});
  }, []);

  const syncNow = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await apiFetch<NonNullable<typeof syncStatus> & { ok: boolean; error?: string | null }>(
        "/sync/now", { method: "POST" },
      );
      setSyncStatus(res);
      setSyncMessage(res.ok
        ? { ok: true, text: "Synced with the cloud." }
        : { ok: false, text: res.error || "Sync did not complete — see the errors above." });
    } catch (e) {
      setSyncMessage({ ok: false, text: e instanceof Error ? e.message : "Sync failed." });
    } finally {
      setSyncing(false);
    }
  };

  // Claude CLI
  const [cliInfo, setCliInfo] = useState<{
    active_path: string | null;
    source: string;
    configured_path: string | null;
    system_path: string | null;
    bundled_path: string | null;
    bundled_available: boolean;
  } | null>(null);
  const [credSources, setCredSources] = useState<CredentialSources | null>(null);
  const [cliPathInput, setCliPathInput] = useState("");
  const [cliSaving, setCliSaving] = useState(false);
  const [cliMessage, setCliMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    loadSettings();
    apiFetch<typeof systemInfo>("/settings/system-info").then(setSystemInfo).catch(() => {});
    apiFetch<CredentialSources>("/settings/credential-sources")
      .then(setCredSources)
      .catch(() => {});
    loadCliInfo();
    loadSyncStatus();
  }, [loadSettings, loadSyncStatus]);

  const loadCliInfo = () => {
    apiFetch<NonNullable<typeof cliInfo>>("/settings/claude-cli")
      .then((info) => {
        setCliInfo(info);
        setCliPathInput(info.configured_path ?? "");
      })
      .catch(() => {});
  };

  const saveCliPath = async () => {
    setCliSaving(true);
    setCliMessage(null);
    try {
      await apiFetch("/settings/claude-cli", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings: { claude_cli_path: cliPathInput } }),
      });
      await loadCliInfo();
      setCliMessage({ ok: true, text: "Saved. CLI path updated." });
    } catch (e) {
      setCliMessage({ ok: false, text: (e as Error).message });
    } finally {
      setCliSaving(false);
    }
  };

  // Keys verified via user tokens (not stored settings) — skip the "not configured" guard
  const TOKEN_BASED_CHECKS = new Set(["discovery_connection"]);

  const checkConnection = async (key: string) => {
    if (!TOKEN_BASED_CHECKS.has(key)) {
      // Read the *live* store value, not the closed-over `settings` snapshot.
      // After a save-then-verify the parent's captured closure still holds the
      // pre-save snapshot (key not set yet), which would short-circuit this to
      // a spurious "Not configured" cross on the first verify. getState() always
      // reflects the just-saved key.
      const setting = useSettingsStore.getState().settings[key];
      if (!setting?.set) {
        setConnResults((r) => ({ ...r, [key]: { ok: false, text: "Not configured", checking: false } }));
        return;
      }
    }
    setConnResults((r) => ({ ...r, [key]: { ok: false, text: "", checking: true } }));
    try {
      const res = await apiFetch<{ verified: boolean; message: string }>("/settings/verify", {
        method: "POST",
        body: JSON.stringify({ key, value: "__use_stored__" }),
      });
      setConnResults((r) => ({ ...r, [key]: { ok: res.verified, text: res.message, checking: false } }));
    } catch (e) {
      setConnResults((r) => ({ ...r, [key]: { ok: false, text: (e as Error).message, checking: false } }));
    }
  };

  const checkAllConnections = () => {
    for (const c of CONNECTION_CHECKS) checkConnection(c.key);
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-space-40">Loading settings...</div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-space">Settings</h2>
          <p className="text-sm text-space-50 mt-0.5">Manage credentials, verify connections, and configure the agent.</p>
        </div>

        {/* Tab bar — horizontally scrollable with edge arrows so tabs never
            wrap onto a second line on narrow windows. */}
        <SettingsTabBar tabs={TABS} active={tab} onSelect={setTab} />

        {/* Tab content */}
        {tab === "general" && (
          <div className="space-y-8">
            {/* Profile — read-only identity from the signed-in session */}
            <Section title="Profile" desc="Your Nbyula identity, used across CoWork.">
              <div className="flex items-center gap-4 rounded-xl border border-space-30 bg-surface px-4 py-3.5">
                <ProfileAvatar name={user?.name} email={user?.email} avatarUrl={user?.avatar_url} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-space truncate">{user?.name || "Signed in"}</p>
                  {user?.email && <p className="text-xs text-space-50 truncate">{user.email}</p>}
                </div>
                {role && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                    role === "ADMIN" ? "bg-jove/10 text-jove" : "bg-space-20 text-space-50"
                  }`}>
                    {role}
                  </span>
                )}
              </div>
            </Section>

            {/* Preferences — device-local appearance */}
            <Section title="Preferences" desc="Personalize how CoWork looks on this device.">
              <div className="flex items-center justify-between gap-4 rounded-xl border border-space-30 bg-surface px-4 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-space">Appearance</p>
                  <p className="text-xs text-space-50 mt-0.5">
                    Follow your system setting, or force light or dark.
                  </p>
                </div>
                <AppearanceToggle />
              </div>
            </Section>
          </div>
        )}

        {tab === "connections" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-space-50">Configure and verify service connections.</p>
              <Button variant="secondary" size="sm" onClick={checkAllConnections}>
                Check All
              </Button>
            </div>

            <div className="space-y-2">
              {CONNECTION_CHECKS.map((c) => {
                const result = connResults[c.key];
                const isSet = settings[c.key]?.set || TOKEN_BASED_CHECKS.has(c.key);
                const fieldMap = CONNECTION_FIELD_MAP[c.key];
                const hasConfigFields = (fieldMap?.secrets.length ?? 0) > 0 || (fieldMap?.configs.length ?? 0) > 0;
                return (
                  <div
                    key={c.key}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-space-30 bg-surface"
                  >
                    <span className="text-lg w-5 h-5 flex items-center justify-center shrink-0 text-space">
                      {c.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-space">{c.label}</p>
                        {(() => {
                          const src = credSources?.sources[c.key]?.source;
                          const badge = src ? SOURCE_BADGE[src] : null;
                          if (!badge) return null;
                          return (
                            <span className={`text-xs leading-none px-2 py-0.5 rounded-full font-medium tracking-wide ${badge.tone}`}>
                              {badge.text}
                            </span>
                          );
                        })()}
                      </div>
                      {result ? (
                        <p className={`text-xs mt-0.5 ${result.ok ? "text-emerald-600" : "text-hydra"}`}>
                          {result.text}
                        </p>
                      ) : (
                        <p className="text-xs text-space-40 mt-0.5">
                          {isSet ? "Configured — click verify to test" : "Not configured — click configure to set up"}
                        </p>
                      )}
                      {/* Claude's shared credential is a POOL: an all-capped pool
                          is a different problem from a missing token, and used to
                          be indistinguishable from one in this UI. */}
                      {c.key === "claude_token" && (credSources?.claude_pool?.length ?? 0) > 0 && (
                        <p className="text-xs text-space-40 mt-0.5">
                          {describePool(credSources!.claude_pool)}
                        </p>
                      )}
                    </div>
                    {/* Status indicator */}
                    <div className="shrink-0">
                      {result?.checking ? (
                        <div className="w-5 h-5 border-2 border-jove border-t-transparent rounded-full animate-spin" />
                      ) : result ? (
                        result.ok ? (
                          <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-hydra" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )
                      ) : (
                        <span className={`w-2.5 h-2.5 rounded-full ${isSet ? "bg-space-40" : "bg-space-30"}`} />
                      )}
                    </div>
                    {hasConfigFields && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setConfigureKey(c.key)}
                      >
                        Configure
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => checkConnection(c.key)}
                      disabled={(!isSet && !TOKEN_BASED_CHECKS.has(c.key)) || result?.checking}
                    >
                      Verify
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Configure modal */}
        {configureKey && (
          <ConfigureModal
            connectionKey={configureKey}
            settings={settings}
            onClose={() => setConfigureKey(null)}
            onSaved={async () => {
              await loadSettings();
              // Auto-verify after save
              checkConnection(configureKey);
            }}
            onDeleted={(key) =>
              // Drop the now-stale verify badge — the credential is gone.
              setConnResults((r) => {
                const next = { ...r };
                delete next[key];
                return next;
              })
            }
          />
        )}

        {tab === "connections" && cloudMode && canEditGlobal && <ProvisionedKeysSection />}

        {tab === "mcp" && <McpServersSection />}

        {tab === "skills" && <SkillsSection />}

        {tab === "advanced" && (
          <div className="space-y-6">
            {/* System Info */}
            <Section title="About" desc="Build and runtime information.">
              {systemInfo ? (
                <div className="rounded-xl border border-space-30 p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                    <InfoRow label="Version" value={`v${systemInfo.app_version}`} />
                    <InfoRow label="Platform" value={`${systemInfo.platform} (${systemInfo.arch})`} />
                    <InfoRow label="Python" value={systemInfo.python_version} />
                    <InfoRow label="Build" value={systemInfo.frozen ? "Packaged" : "Development"} />
                  </div>
                  <div className="border-t border-space-20 pt-3 mt-2">
                    <p className="text-xs font-medium text-space-40 mb-2">Dependencies</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                      {Object.entries(systemInfo.packages).map(([name, ver]) => (
                        <InfoRow key={name} label={name} value={ver} mono />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-space-40">Loading...</p>
              )}
            </Section>

            {/* Cloud sync — status + explicit trigger. The support panel for
                "sync isn't working": last success per direction, the last
                error verbatim, pending local changes, and a manual fallback. */}
            <Section title="Cloud sync" desc="This device's sync with the OmniWerk cloud — status, last errors, and a manual trigger.">
              {syncStatus === null ? (
                <p className="text-xs text-space-40">Loading...</p>
              ) : syncStatus.role === "cloud" ? (
                <p className="text-xs text-space-40">
                  You're on the cloud workspace — it is the sync hub; your devices push to and pull from it.
                </p>
              ) : (
                <div className="rounded-xl border border-space-30 p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                    <InfoRow label="Last push (this device → cloud)" value={formatSyncTime(syncStatus.last_push_at)} />
                    <InfoRow label="Last pull (cloud → this device)" value={formatSyncTime(syncStatus.last_pull_at)} />
                    <InfoRow
                      label="Pending local changes"
                      value={syncStatus.pending_changes ? String(syncStatus.pending_changes) : "none"}
                    />
                  </div>
                  {(syncStatus.last_push_error || syncStatus.last_pull_error) && (
                    <div className="rounded-lg border border-hydra/30 bg-hydra-50 p-3 space-y-1">
                      {syncStatus.last_push_error && (
                        <p className="text-xs text-hydra">
                          <span className="font-semibold">Push error</span>
                          {syncStatus.last_push_error_at ? ` (${formatSyncTime(syncStatus.last_push_error_at)})` : ""}: {syncStatus.last_push_error}
                        </p>
                      )}
                      {syncStatus.last_pull_error && (
                        <p className="text-xs text-hydra">
                          <span className="font-semibold">Pull error</span>
                          {syncStatus.last_pull_error_at ? ` (${formatSyncTime(syncStatus.last_pull_error_at)})` : ""}: {syncStatus.last_pull_error}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3 border-t border-space-20 pt-3">
                    <Button onClick={syncNow} disabled={syncing}>
                      {syncing ? "Syncing..." : "Sync now"}
                    </Button>
                    {syncMessage && (
                      <p className={`text-xs ${syncMessage.ok ? "text-emerald-500" : "text-hydra"}`}>
                        {syncMessage.text}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Section>

            {/* Claude CLI */}
            <Section title="Claude Code CLI" desc="OmniWerk ships a bundled Claude Code CLI via claude-agent-sdk. Point at a system install here if you want a fresher version.">
              {cliInfo ? (
                <div className="rounded-xl border border-space-30 p-4 space-y-3">
                  {/* Status indicator */}
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block h-2.5 w-2.5 rounded-full ${
                        cliInfo.active_path ? "bg-emerald-500" : "bg-red-400"
                      }`}
                    />
                    <span className="text-sm text-space">
                      {cliInfo.active_path
                        ? `Using ${cliInfo.source} CLI`
                        : "Claude CLI not found"}
                    </span>
                  </div>

                  {/* Resolution details */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                    <InfoRow label="Active" value={cliInfo.active_path ?? "none"} mono />
                    <InfoRow label="Source" value={cliInfo.source} />
                    {cliInfo.system_path && (
                      <InfoRow label="System" value={cliInfo.system_path} mono />
                    )}
                    <InfoRow
                      label="Bundled"
                      value={cliInfo.bundled_available ? "available" : "not bundled"}
                    />
                  </div>

                  {/* Manual path override */}
                  <div className="border-t border-space-20 pt-3 mt-2 space-y-2">
                    <p className="text-xs font-medium text-space-40">
                      Custom CLI Path <span className="font-normal">(optional override)</span>
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={cliPathInput}
                        onChange={(e) => setCliPathInput(e.target.value)}
                        placeholder="/usr/local/bin/claude"
                        className="flex-1 rounded-lg border border-space-30 bg-space-10 px-3 py-1.5 text-xs font-mono text-space placeholder:text-space-30 focus:border-jove focus:outline-none"
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={cliSaving}
                        onClick={saveCliPath}
                      >
                        Save
                      </Button>
                      {cliPathInput && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setCliPathInput("");
                            // Save empty to clear override
                            apiFetch("/settings/claude-cli", {
                              method: "PUT",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ settings: { claude_cli_path: "" } }),
                            }).then(() => loadCliInfo());
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-space-40">
                      Leave empty to auto-detect. Resolution order: custom path → system install → SDK-bundled binary.
                    </p>
                    {cliMessage && (
                      <p className={`text-xs flex items-center gap-1 ${cliMessage.ok ? "text-emerald-600" : "text-hydra"}`}>
                        {cliMessage.ok ? (
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        {cliMessage.text}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-space-40">Loading...</p>
              )}
            </Section>

            {/* Nbyula Discovery CLI */}
            <Section title="Nbyula Discovery CLI" desc="The nbyula CLI the agent drives via Bash. Pinned to the version CoWork bundles; update or revert it explicitly below.">
              <NbyulaCliSection />
            </Section>

            {/* Nbyula Gateway MCP */}
            <Section title="Nbyula Gateway MCP" desc="Re-discover tools from the Nbyula Gateway after a server redeployment.">
              <div className="rounded-xl border border-space-30 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Button
                    variant="secondary" size="sm" loading={mcpReinitializing}
                    onClick={async () => {
                      setMcpReinitializing(true); setMcpMessage(null);
                      try {
                        const res = await apiFetch<{ success: boolean; message: string; tool_count?: number; tools?: string[] }>(
                          "/settings/reinitialize-mcp", { method: "POST" },
                        );
                        setMcpMessage({ ok: res.success, text: res.message });
                      } catch (e) { setMcpMessage({ ok: false, text: (e as Error).message }); }
                      finally { setMcpReinitializing(false); }
                    }}
                  >
                    Re-discover Tools
                  </Button>
                  <span className="text-xs text-space-40">
                    Clears the cached tool list and fetches fresh from the MCP server
                  </span>
                </div>
                {mcpMessage && (
                  <p className={`text-xs flex items-center gap-1 ${mcpMessage.ok ? "text-emerald-600" : "text-hydra"}`}>
                    {mcpMessage.ok ? (
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    {mcpMessage.text}
                  </p>
                )}
              </div>
            </Section>

            {/* User */}
            {role && (
              <Section title="User" desc="">
                <div className="rounded-xl border border-space-30 px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                    role === "ADMIN"
                      ? "bg-jove/10 text-jove"
                      : "bg-space-20 text-space-50"
                  }`}>
                    {role}
                  </span>
                </div>
              </Section>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

// --- Helper components ---

/**
 * Horizontally-scrollable tab strip with edge scroll arrows.
 *
 * A plain flex row of tabs wraps onto a second line on narrow windows (ugly,
 * and shoves the content below down). This keeps every tab on one line inside
 * an ``overflow-x-auto`` track and surfaces left/right chevron buttons only
 * when there's more to scroll in that direction. The arrows are absolutely
 * positioned over a short page-coloured gradient fade, so toggling them never
 * shifts the tab layout. The selected tab is scrolled into view when it
 * changes, so choosing an off-screen tab doesn't leave it clipped.
 */
function SettingsTabBar({
  tabs,
  active,
  onSelect,
}: {
  tabs: { id: Tab; label: string; icon: string }[];
  active: Tab;
  onSelect: (id: Tab) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 1px slack absorbs sub-pixel rounding so a fully-scrolled track doesn't
    // leave a phantom arrow enabled.
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    // Recompute when the window (and thus the track width) resizes.
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
    };
  }, [updateArrows]);

  // Keep the selected tab visible — selecting "Advanced" on a narrow window
  // scrolls it into the track rather than leaving it clipped off the edge.
  useEffect(() => {
    const el = scrollRef.current;
    el?.querySelector<HTMLElement>(`[data-tab="${active}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active]);

  const scrollByDir = (dir: -1 | 1) => {
    scrollRef.current?.scrollBy({ left: dir * 160, behavior: "smooth" });
  };

  return (
    <div className="relative mb-6 border-b border-space-30">
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollByDir(-1)}
          aria-label="Scroll tabs left"
          className="absolute inset-y-0 left-0 z-10 flex items-center pl-0.5 pr-6
                     bg-gradient-to-r from-space-20 via-space-20 to-transparent"
        >
          <TabArrowChip dir="left" />
        </button>
      )}

      <div ref={scrollRef} className="flex gap-1 overflow-x-auto no-scrollbar scroll-smooth">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-tab={t.id}
            onClick={() => onSelect(t.id)}
            className={`relative shrink-0 whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors
              ${active === t.id ? "text-jove" : "text-space-40 hover:text-space"}`}
          >
            <span className="mr-1.5">{t.icon}</span>
            {t.label}
            {active === t.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-jove rounded-full" />
            )}
          </button>
        ))}
      </div>

      {canRight && (
        <button
          type="button"
          onClick={() => scrollByDir(1)}
          aria-label="Scroll tabs right"
          className="absolute inset-y-0 right-0 z-10 flex items-center pr-0.5 pl-6
                     bg-gradient-to-l from-space-20 via-space-20 to-transparent"
        >
          <TabArrowChip dir="right" />
        </button>
      )}
    </div>
  );
}

/** "3m ago"-style label for sync timestamps; "never (this session)" for null
 *  (the status registry is in-memory — it repopulates within one ~15s tick). */
function formatSyncTime(iso: string | null | undefined): string {
  if (!iso) return "never (this session)";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return iso;
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Circular chip housing the scroll chevron — same treatment both directions. */
function TabArrowChip({ dir }: { dir: "left" | "right" }) {
  return (
    <span className="flex h-6 w-6 items-center justify-center rounded-full border border-space-30 bg-surface text-space-50 shadow-sm hover:text-space transition-colors">
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d={dir === "left" ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
      </svg>
    </span>
  );
}

/** Avatar image when available, else brand-tinted initials from name/email. */
function ProfileAvatar({ name, email, avatarUrl }: { name?: string; email?: string; avatarUrl?: string | null }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt={name || "Avatar"} className="h-11 w-11 rounded-xl object-cover shrink-0" />;
  }
  const source = (name || email || "?").trim();
  const initials = source
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-jove-50 text-sm font-semibold text-jove">
      {initials}
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-space-40 truncate">{label}</span>
      <span className={`text-xs text-space truncate text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function Section({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-space">{title}</h3>
        <p className="text-xs text-space-40 mt-0.5">{desc}</p>
      </div>
      <div className="space-y-3 pl-0.5">
        {children}
      </div>
    </div>
  );
}

// --- Configure Modal ---

interface ConfigureModalProps {
  connectionKey: string;
  settings: Record<string, import("@/api/types").SettingValue>;
  onClose: () => void;
  onSaved: () => void;
  /** Called after a stored secret is removed, so the parent can drop the
   *  connection's stale verify result (the row is no longer configured). */
  onDeleted: (connectionKey: string) => void;
}

// Per-user keys — everything else in the modal writes SHARED (global) config,
// which the backend restricts to admins on cloud. Mirrors USER_SCOPED_KEYS.
const USER_SCOPED_FIELD_KEYS = new Set(["openrouter_api_key", "setup_complete"]);

function ConfigureModal({ connectionKey, settings, onClose, onSaved, onDeleted }: ConfigureModalProps) {
  const { updateSettings, deleteSetting, canEditGlobal } = useSettingsStore();
  // Which secret field is awaiting delete confirmation (inline, no native dialog).
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);
  // Set when a personal delete found nothing and the shared credential is the
  // only one left — an admin-only, explicitly-confirmed second step.
  const [offerSharedDelete, setOfferSharedDelete] = useState(false);
  const fieldMap = CONNECTION_FIELD_MAP[connectionKey] ?? { secrets: [], configs: [] };
  const connInfo = CONNECTION_CHECKS.find((c) => c.key === connectionKey)!;

  const secrets = SECRET_FIELDS.filter((f) => fieldMap.secrets.includes(f.key));
  const configs = CONFIG_FIELDS.filter((f) => fieldMap.configs.includes(f.key));
  // Shared-infrastructure connection a non-admin can't edit on cloud: show a
  // read-only note instead of letting Save round-trip into a 403.
  const locked =
    !canEditGlobal &&
    [...fieldMap.secrets, ...fieldMap.configs].some((k) => !USER_SCOPED_FIELD_KEYS.has(k));

  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of configs) {
      initial[f.key] = settings[f.key]?.value ?? f.default;
    }
    return initial;
  });

  const [saving, setSaving] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});

  const handleSaveAndVerify = async () => {
    setSaving(true);
    setVerifyResult(null);
    try {
      // Build updates: only include secrets that have a value (non-empty), always include configs
      const updates: Record<string, string> = {};
      for (const f of secrets) {
        if (values[f.key]?.trim()) updates[f.key] = values[f.key]!.trim();
      }
      for (const f of configs) {
        updates[f.key] = values[f.key] ?? f.default;
      }

      // Save if there's anything to save
      if (Object.keys(updates).length > 0) {
        await updateSettings(updates);
      }

      // Verify the connection using stored value
      const res = await apiFetch<{ verified: boolean; message: string }>("/settings/verify", {
        method: "POST",
        body: JSON.stringify({ key: connectionKey, value: "__use_stored__" }),
      });
      setVerifyResult({ ok: res.verified, text: res.message });

      if (res.verified) {
        // Refresh + re-verify the connection row in the parent, then close
        // the modal — the user asked for a one-click "it's verified, done".
        // On failure we keep the modal open (below) so they can fix the key.
        onSaved();
        onClose();
      }
    } catch (e) {
      setVerifyResult({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setVerifyResult(null);
    try {
      const updates: Record<string, string> = {};
      for (const f of secrets) {
        if (values[f.key]?.trim()) updates[f.key] = values[f.key]!.trim();
      }
      for (const f of configs) {
        updates[f.key] = values[f.key] ?? f.default;
      }
      if (Object.keys(updates).length > 0) {
        await updateSettings(updates);
        setVerifyResult({ ok: true, text: "Settings saved" });
        // Clear secret inputs after save
        setValues((v) => {
          const next = { ...v };
          for (const f of secrets) delete next[f.key];
          return next;
        });
        onSaved();
      }
    } catch (e) {
      setVerifyResult({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  // Remove a stored secret (the DB row) after inline confirmation. Used to
  // clear a credential that's no longer needed — e.g. the Claude token once a
  // workspace has fully moved to OpenRouter — so CoWork stops injecting it.
  const handleDelete = async (
    fieldKey: string, fieldLabel: string, scope?: "personal" | "shared",
  ) => {
    setSaving(true);
    setVerifyResult(null);
    setOfferSharedDelete(false);
    try {
      const result = await deleteSetting(fieldKey, scope);
      // Nothing of the caller's own to remove. On the cloud an admin's token
      // may be the ORG's rather than theirs, and clearing that is deliberate,
      // never a fall-through — so offer it explicitly instead of silently
      // taking Claude away from everyone (ADR-0014 amendment 6).
      if (!result.deleted && result.scope === "personal" && canEditGlobal) {
        setOfferSharedDelete(true);
        setVerifyResult({
          ok: false,
          text: `No personal ${fieldLabel} to remove.`,
        });
        return;
      }
      setConfirmDeleteKey(null);
      setValues((v) => {
        const next = { ...v };
        delete next[fieldKey];
        return next;
      });
      setVerifyResult({ ok: true, text: `Removed stored ${fieldLabel}.` });
      onDeleted(connectionKey);
    } catch (e) {
      setVerifyResult({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-space-30">
          <span className="text-xl">{connInfo.icon}</span>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-space">Configure {connInfo.label}</h3>
            <p className="text-xs text-space-40">Update credentials and settings for this connection.</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-space-40 hover:text-space hover:bg-space-20 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {connectionKey === "openrouter_api_key" &&
            settings.openrouter_key_provenance?.value === "provisioned" && (
              <div className="rounded-lg bg-jove-10 border border-jove-30 px-3 py-2 text-sm text-space-50">
                This key was <span className="font-semibold text-space">provisioned
                automatically by OmniWerk</span> from the org workspace. Paste your
                own key below to switch to it — yours always wins.
              </div>
            )}
          {locked && (
            <div className="rounded-lg bg-space-10 border border-space-30 px-3 py-2 text-sm text-space-40">
              This is a shared workspace setting managed by your admins — reach
              out to them to change it.
            </div>
          )}
          {!locked && secrets.map((field) => {
            const existing = settings[field.key];
            return (
              <div key={field.key} className="space-y-1.5">
                <label className="block text-sm font-medium text-space">{field.label}</label>
                {existing?.set && (
                  confirmDeleteKey === field.key ? (
                    offerSharedDelete ? (
                      // Nothing personal to remove — the only token here is the
                      // organisation's. Say who it affects before touching it.
                      <div className="flex items-center gap-2 text-xs flex-wrap">
                        <span className="text-hydra">
                          Clear the organisation's shared {field.label}? Everyone loses it.
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDelete(field.key, field.label, "shared")}
                          disabled={saving}
                          className="font-medium text-hydra hover:underline disabled:opacity-50"
                        >
                          Clear for everyone
                        </button>
                        <button
                          type="button"
                          onClick={() => { setOfferSharedDelete(false); setConfirmDeleteKey(null); }}
                          disabled={saving}
                          className="text-space-40 hover:text-space disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-hydra">Remove the stored {field.label}?</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(field.key, field.label)}
                        disabled={saving}
                        className="font-medium text-hydra hover:underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteKey(null)}
                        disabled={saving}
                        className="text-space-40 hover:text-space disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                    )
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <p className="flex-1 min-w-0 text-xs text-emerald-600 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                        <span className="truncate">Current: {existing.masked}</span>
                      </p>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteKey(field.key)}
                        title={`Remove stored ${field.label}`}
                        aria-label={`Remove stored ${field.label}`}
                        className="shrink-0 p-1 rounded-md text-space-40 hover:text-hydra hover:bg-red-50 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.158-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.043-2.09 1.02-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  )
                )}
                <div className="relative">
                  <input
                    type={showSecret[field.key] ? "text" : "password"}
                    className="w-full px-3 py-2 pr-10 border border-space-30 rounded-lg text-sm font-mono
                               focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                               placeholder:text-space-40 transition-all"
                    value={values[field.key] ?? ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={existing?.set ? "Leave blank to keep current" : field.placeholder}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret((s) => ({ ...s, [field.key]: !s[field.key] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-space-40 hover:text-space transition-colors"
                  >
                    {showSecret[field.key] ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Config fields */}
          {!locked && configs.length > 0 && (
            <div className="space-y-3 pt-1">
              {secrets.length > 0 && <hr className="border-space-30" />}
              {configs.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <label className="block text-sm font-medium text-space">{field.label}</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-space-30 rounded-lg text-sm
                               focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                               transition-all"
                    value={values[field.key] ?? field.default}
                    onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Verify result */}
          {verifyResult && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              verifyResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-hydra"
            }`}>
              {verifyResult.ok ? (
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {verifyResult.text}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-space-30 bg-space-10">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {locked ? "Close" : "Cancel"}
          </Button>
          {!locked && (
            <>
              <Button variant="secondary" size="sm" onClick={handleSave} loading={saving}>
                Save
              </Button>
              <Button variant="primary" size="sm" onClick={handleSaveAndVerify} loading={saving}>
                Verify & Save
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
